import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { INTERVIEW_TYPES, type Interview, type InterviewType } from "@/types";

const TYPE_LABELS: Record<InterviewType, string> = {
  phone: "Phone Screen",
  video: "Video Call",
  onsite: "On-Site",
  technical: "Technical",
  behavioral: "Behavioral",
  final: "Final Round",
};

function toLocalInput(iso: string) {
  return iso.slice(0, 16);
}

function toLocalDefault(d: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:00`;
}

interface Props {
  defaultDate?: Date;
  interview?: Interview;
  onClose: () => void;
  onSaved: () => void;
}

export function InterviewFormModal({ defaultDate, interview, onClose, onSaved }: Props) {
  const qc = useQueryClient();
  const isEdit = !!interview;

  const [candidateId, setCandidateId] = useState(String(interview?.candidate_id ?? ""));
  const [scheduledAt, setScheduledAt] = useState(
    interview ? toLocalInput(interview.scheduled_at) : (defaultDate ? toLocalDefault(defaultDate) : "")
  );
  const [duration, setDuration] = useState(interview?.duration_minutes ?? 60);
  const [type, setType] = useState<InterviewType>(interview?.interview_type ?? "video");
  const [location, setLocation] = useState(interview?.location ?? "");
  const [interviewer, setInterviewer] = useState(interview?.interviewer_name ?? "");
  const [notes, setNotes] = useState(interview?.notes ?? "");

  const { data: candidatesData } = useQuery({
    queryKey: ["candidates-for-calendar"],
    queryFn: () => api.listCandidates(),
    staleTime: 60_000,
  });
  const candidates = candidatesData?.candidates ?? [];

  const save = useMutation<unknown, Error, void>({
    mutationFn: () => {
      const body = {
        scheduled_at: new Date(scheduledAt).toISOString(),
        duration_minutes: duration,
        interview_type: type,
        location: location || undefined,
        interviewer_name: interviewer || undefined,
        notes: notes || undefined,
      };
      return isEdit
        ? api.updateInterview(interview.id, body)
        : api.createInterview({ candidate_id: Number(candidateId), ...body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["interviews"] });
      onSaved();
    },
  });

  const inputCls = "w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus";
  const labelCls = "mb-1 block text-xs font-semibold text-text-secondary";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-card bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="mb-4 text-base font-semibold text-near-black">
          {isEdit ? "Edit Interview" : "Schedule Interview"}
        </h2>

        <div className="space-y-3">
          {!isEdit && (
            <div>
              <label className={labelCls}>Candidate</label>
              <select value={candidateId} onChange={(e) => setCandidateId(e.target.value)} className={inputCls}>
                <option value="">— select candidate —</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Date & Time</label>
              <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Duration (min)</label>
              <input type="number" value={duration} min={15} step={15} onChange={(e) => setDuration(Number(e.target.value))} className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Type</label>
            <select value={type} onChange={(e) => setType(e.target.value as InterviewType)} className={inputCls}>
              {INTERVIEW_TYPES.map((t) => (
                <option key={t} value={t}>{TYPE_LABELS[t]}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Location</label>
              <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Zoom / Office" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Interviewer</label>
              <input value={interviewer} onChange={(e) => setInterviewer(e.target.value)} placeholder="Name" className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={`${inputCls} resize-none`} />
          </div>
        </div>

        {save.isError && (
          <p className="mt-3 text-xs text-red-600">{String(save.error)}</p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-btn px-4 py-2 text-sm text-text-secondary hover:bg-surface-secondary">
            Cancel
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending || (!isEdit && !candidateId) || !scheduledAt}
            className="rounded-btn bg-near-black px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : isEdit ? "Save changes" : "Schedule"}
          </button>
        </div>
      </div>
    </div>
  );
}
